import {
  CloseButton,
  type CustomUseToastOptions,
  InlineNotification,
  type UseToast,
} from '@lego/klik-ui';

/** The toast dispatcher returned by `useToast()`. */
type ToastFn = ReturnType<UseToast>;

/**
 * Show a toast while avoiding duplicates. Chakra/klik-ui's `useToast` does not
 * dedupe on its own — calling it repeatedly with the same `id` stacks multiple
 * toasts (and triggers React "duplicate key" warnings). This helper skips the
 * toast when one with the same `id` is already active, so identical error
 * toasts (e.g. repeated "Forbidden") collapse into a single toast.
 */
export function showToast(toast: ToastFn, options: CustomUseToastOptions) {
  if (options.id !== undefined && toast.isActive(options.id)) {
    return options.id;
  }
  return toast(options);
}

export class ToastOptions {
  private static readonly position = 'top-right';
  private static readonly duration = null;

  private static errorId(title: string, msg: string): string {
    return `error:${JSON.stringify([title, msg])}`;
  }

  public static error(
    title: string,
    msg: string,
    action: React.JSX.Element | undefined = undefined
  ): CustomUseToastOptions {
    return {
      id: ToastOptions.errorId(title, msg),
      duration: ToastOptions.duration,
      position: ToastOptions.position,
      render: ({ onClose }) => (
        <InlineNotification variant="error">
          <InlineNotification.Content alignItems="flex-start" flexDirection="column">
            <InlineNotification.Title>{title}</InlineNotification.Title>
            <InlineNotification.Description>
              {msg} {action ? action : undefined}
            </InlineNotification.Description>
          </InlineNotification.Content>
          <CloseButton onClick={onClose} />
        </InlineNotification>
      ),
    };
  }

  public static success(title: string, msg: string, duration?: number): CustomUseToastOptions {
    return {
      duration: duration ? duration : ToastOptions.duration,
      position: ToastOptions.position,
      render: ({ onClose }) => (
        <InlineNotification variant="success">
          <InlineNotification.Content alignItems="flex-start" flexDirection="column">
            <InlineNotification.Title>{title}</InlineNotification.Title>
            <InlineNotification.Description>{msg}</InlineNotification.Description>
          </InlineNotification.Content>
          <CloseButton onClick={onClose} />
        </InlineNotification>
      ),
    };
  }
}
